import React, { useCallback } from "react"
import { Tooltip } from "react-tooltip"
import { ISharedContext, IActivatable, SetFormValue, Dispatch } from "../support/registration_form_component"
import * as data from "../data"
import { IClientConfig, TPAUse, PBMUse, CarveOutPayerUse } from "../clients/config"
import { RadioButtonGroup } from "../support/radio_buttons"
import { NaicFormGroups } from "../support/naic_form_groups"
import { FormGroup } from "../support/layout"
import { FeinFormGroups } from "../support/fein_form_groups"
import { serializeRegExp } from "../support/helpers"
import { TpaSelfInsuredForm } from "../support/tpa_self_insured_form"
import { InsurerTypeName } from "../clients/config"

type RegistrationClaimsProps = ISharedContext & IActivatable & {
  children: React.ReactNode
}

export function RegistrationClaims(props: RegistrationClaimsProps) {
  if (!props.active) {
    return <div />
  }

  return (
    <div className="step" id="step-2">
      <h2 className="visible-print">
        Step 4: Submissions Information &amp; Comments
      </h2>
      {props.children}
    </div>
  )
}

export interface ITpaProps {
  client: data.IClient
  config: IClientConfig
  form: data.IRegistrationForm | null
  active_submitters: data.IActiveSubmitter[]
  setFormValue: SetFormValue
  dispatch: Dispatch
}

export function Tpa({
  client,
  config,
  form,
  active_submitters,
  setFormValue,
  dispatch,
}: ITpaProps) {
  const onRadioChange = useCallback((value: string) => {
    setFormValue(["tpa", "work-with"], value)
  }, [setFormValue])

  // This method of updating the TPA is used to allow array deletions.
  // It's also more type-safe and controlled, and may be the preferred way
  // in the future as long as React keeps the state changes ordered correctly.
  const onMoreInfoChange = useCallback((tpa: Partial<data.ITPA>) => dispatch({
    action: "UPDATE_FORM",
    form: {
      tpa: {
        ...form?.tpa,
        ...tpa,
      },
    },
  }), [dispatch, form?.tpa])

  if (
    !config.tpaUseQuestion ||
    !client.has_tpa ||
    !data.HasCarveOutSections.has(form?.insurer?.type as InsurerTypeName)
  ) {
    return null
  }

  const tpa: data.ITPA = form?.tpa || {}
  const tpaUseSelection = tpa["work-with"]

  return (
    <div className="panel panel-default show-medical-claims-reporting">
      <div className="panel-heading">
        <h1 className="panel-title">Use of a TPA</h1>
        {!config.tpaUseQuestion.options.find(option => option.value === TPAUse.WE_ARE_THE_TPA)?.hidden && (
          <p className="panel-description">
            Note: If you are a TPA that works with more than one organization -- or a healthcare organization that contracts
            with more than one TPA -- please use the Comments field below to provide the required information (Name, NAIC,
            FEIN, and which organization will be submitting data) for these additional organizations to ensure these
            relationships are appropriately documented in our system.
          </p>
        )}
      </div>
      <div className="panel-body form-horizontal">
        <RadioButtonGroup
          title={config.tpaUseQuestion.question}
          name="tpa[work-with]"
          required={true}
          value={tpaUseSelection}
          options={config.tpaUseQuestion.options.filter(option => !option.hidden).map((option) => {
            return {
              value: option.value,
              label: option.label + (option.note ? ` Note: ${option.note}` : ""),
            }
          })}
          onChange={onRadioChange}
          style={{paddingBottom: "16px"}}
        />
        {(tpaUseSelection === TPAUse.WE_ARE_THE_TPA || tpaUseSelection === TPAUse.WE_HAVE_A_TPA) && (
          <TpaMoreInfoFields
            tpa={tpa}
            active_submitters={active_submitters}
            organizationNameTooltip={config.tpaUseQuestion.options.find(option => option.value === tpaUseSelection)?.organizationNameTooltip}
            onChange={onMoreInfoChange}
          />
        )}

        {config.showTpaSelfInsuredForm && tpaUseSelection === TPAUse.WE_ARE_THE_TPA && (
          <TpaSelfInsuredForm form={form} dispatch={dispatch} />
        )}
      </div>
    </div>
  )
}

interface TpaMoreInfoFieldsProps {
  tpa: data.ITPA
  active_submitters: data.IActiveSubmitter[]
  organizationNameTooltip?: string
  onChange: (tpa: Partial<data.ITPA>) => void
}
function TpaMoreInfoFields({
  tpa,
  active_submitters,
  organizationNameTooltip,
  onChange,
}: TpaMoreInfoFieldsProps): React.ReactNode {
  const onCompanyNameChange = useCallback((newValue: string) => onChange({
    company_name: newValue,
  }), [onChange])
  const onCustomCompanyNameChange = useCallback((newValue: string) => onChange({
    custom_company_name: newValue,
  }), [onChange])
  const onOrganizationChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => onChange({
    organization: e.currentTarget.value,
  }), [onChange])
  const onNaicChange = useCallback((naics: string[]) => {
    onChange({ naics: naics })
  }, [onChange])
  const onFeinChange = useCallback((feins: string[]) => {
    onChange({ feins: feins })
  }, [onChange])

  const useActiveSubmitterDropdown = active_submitters && active_submitters.length > 0
  const showOther = useActiveSubmitterDropdown && tpa.company_name === data.OTHER

  let naics = tpa.naics
  if (!Array.isArray(naics) || naics.length === 0) {
    naics = [""]
  }

  let feins = tpa.feins
  if (!Array.isArray(feins) || feins.length === 0) {
    feins = [""]
  }

  return (
    <>
      <FormGroup>
        <label className="col-sm-4 control-label required" htmlFor="tpa_company_name">
          Organization Name
          {organizationNameTooltip && (
            <>
              &nbsp;
              <i
                className="fa fa-question-circle"
                data-tooltip-id="tip-tpa-organization-name"
                data-tooltip-content={organizationNameTooltip}
              />
              <Tooltip id="tip-tpa-organization-name" className="registration-tooltip" />
            </>
          )}
        </label>
        <div className="col-sm-8">
          {useActiveSubmitterDropdown ? (
            <ActiveSubmitterSelect
              name="tpa"
              value={tpa.company_name || ""}
              active_submitters={active_submitters}
              onChange={onCompanyNameChange}
            />
          ) : (
            <LegacyCompanyNameInput
              name="tpa"
              value={tpa.company_name || ""}
              onChange={onCompanyNameChange}
            />
          )}
        </div>
      </FormGroup>

      {showOther && (
        <OtherOrganizationNameFormGroup
          name="tpa"
          label="Specify Organization Name"
          value={tpa.custom_company_name || ""}
          onChange={onCustomCompanyNameChange}
        />)}

      <NaicFormGroups
        label="Organization NAIC Number"
        naics={naics}
        idPrefix="tpa_naics"
        labelClass="control-label long-label"
        onChange={onNaicChange}
      />

      <FeinFormGroups
        label="Organization FEIN Number"
        feins={feins}
        idPrefix="tpa_feins"
        labelClass="control-label long-label"
        labelClassRequired="control-label long-label required"
        required={false}
        onChange={onFeinChange}
      />

      <FormGroup>
        <label className="col-sm-7 control-label required" htmlFor="tpa_organization">
          Which organization will be submitting data to Onpoint?
        </label>

        <div className="col-sm-5">
          <select
            onChange={onOrganizationChange}
            value={tpa?.organization ?? ""}
            name="tpa[organization]"
            id="tpa_organization"
            className="form-control"
            required={true}
          >
            <option />
            <option>The Insurer</option>
            <option>The TPA</option>
          </select>
        </div>
      </FormGroup>
    </>
  )
}

interface LegacyCompanyNameInputProps {
  name: "tpa" | "pbm" | "carve_out_payer"
  value: string
  onChange: (value: string) => void
}
function LegacyCompanyNameInput({
  name,
  value,
  onChange,
}: LegacyCompanyNameInputProps) {
  const changeHandler = useCallback((e: React.ChangeEvent<HTMLInputElement>) => onChange(e.currentTarget.value), [onChange])
  return (
    <input
      name={`${name}[company_name]`}
      id={`${name}_company_name`}
      placeholder=""
      className="form-control"
      onChange={changeHandler}
      value={value}
      required={true}
      maxLength={100}
    />
  )
}

interface ActiveSubmitterSelectProps {
  name: "tpa" | "pbm" | "carve_out_payer"
  value: string
  active_submitters: data.IActiveSubmitter[]
  onChange: (value: string) => void
}
function ActiveSubmitterSelect({
  name,
  value,
  active_submitters,
  onChange,
}: ActiveSubmitterSelectProps) {
  const changeHandler = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => onChange(e.currentTarget.value), [onChange])

  const inputName = `${name}[company_name]`

  return (
    <>
      <label className="sr-only" htmlFor={`${name}_company_name`}>Organization Name</label>
      <select
        id={`${name}_company_name`}
        name={inputName}
        className="form-control"
        value={value}
        onChange={changeHandler}
        required={true}
      >
        <option value=""/>
        <option value={data.OTHER}>Other (Please Specify)</option>
        {active_submitters.map((submitter) => (
          <option
            key={submitter.id}
            value={submitter.companyLongName}
          >
            {submitter.companyLongName}
          </option>
        ))}
      </select>
    </>
  )
}

interface OtherOrganizationNameFormGroupProps {
  name: "tpa" | "pbm" | "carve_out_payer"
  label: string
  value: string
  columnClassNames?: string
  onChange: (value: string) => void
}
function OtherOrganizationNameFormGroup({
  name,
  label,
  value,
  columnClassNames,
  onChange,
}: OtherOrganizationNameFormGroupProps) {
  const changeHandler = useCallback((e: React.ChangeEvent<HTMLInputElement>) => onChange(e.currentTarget.value), [onChange])

  if (!columnClassNames) {
    columnClassNames = "col-sm-8 col-sm-offset-4"
  }

  return (
    <div className="form-group">
      <div className={columnClassNames}>
        <input
          name={`${name}[custom_company_name]`}
          id={`${name}_custom_company_name`}
          placeholder={label}
          aria-label={label}
          className="form-control"
          onChange={changeHandler}
          value={value}
          required={true}
          maxLength={100}
        />
      </div>
    </div>
  )
}

export interface PbmProps {
  config: IClientConfig
  client: data.IClient
  form: data.IRegistrationForm | null
  active_submitters: data.IActiveSubmitter[]
  setFormValue: SetFormValue
  dispatch: Dispatch
}
export function Pbm({
  config,
  client,
  form,
  active_submitters,
  setFormValue,
  dispatch,
}: PbmProps) {
  const onRadioChange = useCallback((value: string) => {
    setFormValue(["pbm", "work-with"], value)
  }, [setFormValue])

  // This method of updating the TPA is used to allow array deletions.
  // It's also more type-safe and controlled, and may be the preferred way
  // in the future as long as React keeps the state changes ordered correctly.
  const onMoreInfoChange = useCallback((pbm: Partial<data.IPBM>) => {
    dispatch({
      action: "UPDATE_FORM",
      form: {
        pbm: {
          ...form?.pbm,
          ...pbm,
        },
      },
    })
  }, [dispatch, form?.pbm])

  if (
    !config.pbmUseQuestion ||
    !client.has_pbm ||
    !data.HasCarveOutSections.has(form?.insurer?.type as InsurerTypeName)
  ) {
    return null
  }

  const pbm: data.IPBM =  form?.pbm ?? {}
  const pbmUseSelection = pbm["work-with"]

  return (
    <div className="panel panel-default show-pharmacy-claims-reporting">
      <div className="panel-heading">
        <h1 className="panel-title">Use of a PBM</h1>
      </div>
      <div className="panel-body form-horizontal">
        <RadioButtonGroup
          title={config.pbmUseQuestion.question}
          name="pbm[work-with]"
          required={true}
          value={pbmUseSelection}
          options={config.pbmUseQuestion.options.filter(option => !option.hidden).map((option) => {
            return {
              value: option.value,
              label: option.label + (option.note ? ` Note: ${option.note}` : ""),
            }
          })}
          onChange={onRadioChange}
          style={{paddingBottom: "16px"}}
        />
        {(pbmUseSelection === PBMUse.WE_ARE_THE_PBM || pbmUseSelection === PBMUse.WE_HAVE_A_PBM) && (
          <PbmMoreInfoFields
            pbm={pbm}
            active_submitters={active_submitters}
            pbmUseSelection={pbmUseSelection}
            showRegistrationNumber={config.showPbmRegistrationNumber ?? false}
            registrationNumberLabel={config.pbmRegistrationNumberLabel}
            organizationNameTooltip={config.pbmUseQuestion.options.find(option => option.value === pbmUseSelection)?.organizationNameTooltip}
            onChange={onMoreInfoChange}
          />
        )}
      </div>
    </div>
  )
}

export interface PbmMoreInfoFieldsProps {
  pbm: data.IPBM
  active_submitters: data.IActiveSubmitter[]
  pbmUseSelection: PBMUse
  showRegistrationNumber: boolean
  registrationNumberLabel?: string
  organizationNameTooltip?: string
  onChange: (pbm: Partial<data.IPBM>) => void
}
function PbmMoreInfoFields({
  pbm,
  active_submitters,
  pbmUseSelection,
  showRegistrationNumber,
  registrationNumberLabel,
  organizationNameTooltip,
  onChange,
}: PbmMoreInfoFieldsProps): React.ReactNode {
  const onCompanyNameChange = useCallback((newValue: string) => onChange({
    company_name: newValue,
  }), [onChange])
  const onCustomCompanyNameChange = useCallback((newValue: string) => onChange({
    custom_company_name: newValue,
  }), [onChange])
  const onOrganizationChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({ organization: e.currentTarget.value })
  }, [onChange])
  const onNaicChange = useCallback((naics: string[]) => {
    onChange({ naics: naics })
  }, [onChange])
  const onFeinChange = useCallback((feins: string[]) => {
    onChange({ feins: feins })
  }, [onChange])
  const onRegistrationNumberChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ pbm_registration_number: e.currentTarget.value })
  }, [onChange])
  // trim whitespace on blur
  const onRegistrationNumberBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    onChange({ pbm_registration_number: e.currentTarget.value.trim() })
  }, [onChange])

  const useActiveSubmitterDropdown = active_submitters && active_submitters.length > 0
  const showOther = useActiveSubmitterDropdown && pbm.company_name === data.OTHER

  let naics = pbm.naics
  if (!Array.isArray(naics) || naics.length === 0) {
    naics = [""]
  }

  let feins = pbm.feins
  if (!Array.isArray(feins) || feins.length === 0) {
    feins = [""]
  }

  const label = registrationNumberLabel ?? "PBM Registration Number"

  // Make this field required for the first yes option, "Yes I am a PBM." but optional
  // for the "Yes, I have a PBM submitting..." and the "No, I am not a PBM..." options.
  // Colorado request in DO-562.
  const registrationNumberRequired = pbmUseSelection === PBMUse.WE_ARE_THE_PBM

  return (
    <>
      <FormGroup>
        <label className="col-sm-4 control-label required" htmlFor="pbm_company_name">
          Organization Name
          {organizationNameTooltip && (
            <>
              &nbsp;
              <i
                className="fa fa-question-circle"
                data-tooltip-id="tip-pbm-organization-name"
                data-tooltip-content={organizationNameTooltip}
              />
              <Tooltip id="tip-pbm-organization-name" className="registration-tooltip" />
            </>
          )}
        </label>
        <div className="col-sm-8">
          {useActiveSubmitterDropdown ? (
            <ActiveSubmitterSelect
              name="pbm"
              value={pbm.company_name ?? ""}
              active_submitters={active_submitters}
              onChange={onCompanyNameChange}
            />
          ) : (
            <LegacyCompanyNameInput
              name="pbm"
              value={pbm.company_name ?? ""}
              onChange={onCompanyNameChange}
            />
          )}
        </div>
      </FormGroup>

      {showOther && (
        <OtherOrganizationNameFormGroup
          name="pbm"
          label="Specify Organization Name"
          value={pbm.custom_company_name ?? ""}
          onChange={onCustomCompanyNameChange}
        />)}

      {showRegistrationNumber && (
        <FormGroup>
          <label className={`col-sm-3 control-label${registrationNumberRequired ? " required" : ""}`} htmlFor="pbm_registration_number">
            {label}
          </label>
          <div className="col-sm-5">
            <input
              name="pbm[pbm_registration_number]"
              id="pbm_registration_number"
              className="form-control"
              maxLength={10}
              pattern="[0-9A-Za-z]*"
              placeholder="Registration Number (10 digits or letters)"
              onChange={onRegistrationNumberChange}
              onBlur={onRegistrationNumberBlur}
              value={pbm.pbm_registration_number ?? ""}
              required={registrationNumberRequired}
              data-fv-notempty={registrationNumberRequired ? "true" : ""}
              data-fv-notempty-message={registrationNumberRequired ? "This field is required if you are a PBM." : ""}
              data-fv-regex-match={serializeRegExp(/^[0-9A-Za-z]{1,10}$/)}
              data-fv-regex-match-error="Must contain only letters and numbers, and be 10 characters or less"
            />
          </div>
        </FormGroup>
      )}

      <NaicFormGroups
        label="Organization NAIC Number"
        naics={naics}
        idPrefix="pbm_naics"
        labelClass="control-label long-label"
        onChange={onNaicChange}
      />

      <FeinFormGroups
        label="Organization FEIN Number"
        feins={feins}
        idPrefix="pbm_feins"
        labelClass="control-label long-label"
        labelClassRequired="control-label long-label required"
        required={false}
        onChange={onFeinChange}
      />

      <FormGroup>
        <label className="col-sm-7 control-label required" htmlFor="pbm_organization">
          Which organization will be submitting data to Onpoint?
        </label>

        <div className="col-sm-5">
          <select
            onChange={onOrganizationChange}
            value={pbm?.organization ?? ""}
            name="pbm[organization]"
            id="pbm_organization"
            className="form-control"
            required={true}
          >
            <option />
            <option>The Insurer</option>
            <option>The PBM</option>
          </select>
        </div>
      </FormGroup>
    </>
  )
}

export interface CarveOutPayerProps {
  config: IClientConfig
  client: data.IClient
  form: data.IRegistrationForm | null
  active_submitters: data.IActiveSubmitter[]
  setFormValue: SetFormValue
  dispatch: Dispatch
}
export const CarveOutPayer: React.FC<CarveOutPayerProps> = ({
  config,
  client,
  form,
  active_submitters,
  setFormValue,
  dispatch,
}) => {
  const onRadioChange = useCallback((value: string) => {
    setFormValue(["carve_out_payer", "work-with"], value)
  }, [setFormValue])

  const onMoreInfoChange = useCallback((carve_out_payer: Partial<data.ICarveOutPayer>) => dispatch({
    action: "UPDATE_FORM",
    form: {
      carve_out_payer: {
        ...form?.carve_out_payer,
        ...carve_out_payer,
      },
    },
  }), [dispatch, form?.carve_out_payer])

  if (
    !config.carveOutPayerUseQuestion ||
    !client.has_carve_out_payer ||
    !data.HasCarveOutSections.has(form?.insurer?.type as InsurerTypeName)
  ) {
    return null
  }

  const carveOutPayer: data.ICarveOutPayer = (form?.carve_out_payer) || {}
  const carveOutPayerUseSelection = carveOutPayer["work-with"]

  return (
    <div className="panel panel-default carve-out-payer">
      <div className="panel-heading">
        <h1 className="panel-title">Use of a Behavioral Health Carve-Out Payer</h1>
      </div>
      <div className="panel-body form-horizontal">
        <RadioButtonGroup
          title={config.carveOutPayerUseQuestion.question}
          name="carve_out_payer[work-with]"
          required={true}
          value={carveOutPayerUseSelection}
          options={config.carveOutPayerUseQuestion.options.filter(option => !option.hidden).map((option) => {
            return {
              value: option.value,
              label: option.label + (option.note ? ` Note: ${option.note}` : ""),
            }
          })}
          onChange={onRadioChange}
          style={{paddingBottom: "16px"}}
        />

        {(carveOutPayerUseSelection === CarveOutPayerUse.WE_ARE_THE_CARVE_OUT_PAYER || carveOutPayerUseSelection === CarveOutPayerUse.WE_HAVE_A_CARVE_OUT_PAYER) && (
          <CarveOutPayerMoreInfoFields
            carve_out_payer={carveOutPayer}
            active_submitters={active_submitters}
            organizationNameTooltip={config.carveOutPayerUseQuestion.options.find(option => option.value === carveOutPayerUseSelection)?.organizationNameTooltip}
            onChange={onMoreInfoChange}
          />
        )}
      </div>
    </div>
  )
}

interface CarveOutPayerMoreInfoFieldsProps {
  carve_out_payer: data.ICarveOutPayer
  active_submitters: data.IActiveSubmitter[]
  organizationNameTooltip?: string
  onChange: (tpa: Partial<data.ICarveOutPayer>) => void
}

function CarveOutPayerMoreInfoFields({
  carve_out_payer,
  active_submitters,
  organizationNameTooltip,
  onChange,
}: CarveOutPayerMoreInfoFieldsProps): React.ReactNode {
  const onCompanyNameChange = useCallback((newValue: string) => onChange({
    company_name: newValue,
  }), [onChange])
  const onCustomCompanyNameChange = useCallback((newValue: string) => onChange({
    custom_company_name: newValue,
  }), [onChange])
  const onOrganizationChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => onChange({
    organization: e.currentTarget.value,
  }), [onChange])
  const onNaicChange = useCallback((naics: string[]) => {
    onChange({ naics: naics })
  }, [onChange])
  const onFeinChange = useCallback((feins: string[]) => {
    onChange({ feins: feins })
  }, [onChange])

  const useActiveSubmitterDropdown = active_submitters && active_submitters.length > 0
  const showOther = useActiveSubmitterDropdown && carve_out_payer.company_name === data.OTHER

  let naics = carve_out_payer.naics
  if (!Array.isArray(naics) || naics.length === 0) {
    naics = [""]
  }

  let feins = carve_out_payer.feins
  if (!Array.isArray(feins) || feins.length === 0) {
    feins = [""]
  }

  return (
    <div className="show-carve-out-payer-work-with">
      <div className="form-group row">
        <label
          className="col-sm-4 control-label long-label required"
          htmlFor="carve_out_payer_company_name"
        >
            Organization Name
          {organizationNameTooltip && (
            <>
                &nbsp;
              <i
                className="fa fa-question-circle"
                data-tooltip-id="tip-carve-out-payer-organization-name"
                data-tooltip-content={organizationNameTooltip}
              />
              <Tooltip id="tip-carve-out-payer-organization-name" className="registration-tooltip" />
            </>
          )}
        </label>

        <div className="col-sm-8">
          {useActiveSubmitterDropdown ? (
            <ActiveSubmitterSelect
              name="carve_out_payer"
              value={carve_out_payer.company_name ?? ""}
              active_submitters={active_submitters}
              onChange={onCompanyNameChange}
            />
          ) : (
            <LegacyCompanyNameInput
              name="carve_out_payer"
              value={carve_out_payer.company_name ?? ""}
              onChange={onCompanyNameChange}
            />
          )}
        </div>
      </div>

      {showOther && (
        <OtherOrganizationNameFormGroup
          name="carve_out_payer"
          label="Specify Organization Name"
          columnClassNames="col-sm-8 col-sm-offset-4"
          value={carve_out_payer.custom_company_name ?? ""}
          onChange={onCustomCompanyNameChange}
        />
      )}

      <NaicFormGroups
        label="Organization NAIC Number"
        naics={naics}
        idPrefix="carve_out_payer_naics"
        labelColumns={4}
        labelClass="control-label long-label"
        onChange={onNaicChange}
      />

      <FeinFormGroups
        label="Organization FEIN Number"
        feins={feins}
        idPrefix="carve_out_payer_feins"
        labelColumns={4}
        labelClass="control-label long-label"
        labelClassRequired="control-label required"
        required={false}
        onChange={onFeinChange}
      />

      <FormGroup>
        <label
          className="col-sm-7 control-label required"
          htmlFor="carve_out_payer_organization"
        >
          Which organization will be submitting data to Onpoint?
        </label>

        <div className="col-sm-5">
          <select
            onChange={onOrganizationChange}
            value={carve_out_payer?.organization ?? ""}
            name="carve_out_payer[organization]"
            id="carve_out_payer_organization"
            className="form-control"
            required={true}
          >
            <option />
            <option>The Insurer</option>
            <option>The Behavioral Health Carve-Out Payer</option>
          </select>
        </div>
      </FormGroup>
    </div>
  )
}


interface ISubmissionScheduleProps extends ISharedContext {
  monthly?: boolean
  quarterly?: boolean
  biannually?: boolean
  annually?: boolean
}
export function SubmissionSchedule(props: ISubmissionScheduleProps) {
  if (!props.client.has_schedule) {
    return null
  }

  return (
    <div className="panel panel-default">
      <div className="panel-heading">
        <h1 className="panel-title">Submission Schedule</h1>
      </div>
      <div className="panel-body">
        <div className="form-group row">
          <label className="col-sm-9 control-label required" htmlFor="submission_schedule_frequency">
              Please indicate your planned schedule for regular submissions:
            {props.client && props.client.slug === "vt" && (
              <span>&nbsp;
                <i className="fa fa-question-circle" data-tooltip-id="tip-submission-frequency" />
                <Tooltip id="tip-submission-frequency" className="registration-tooltip">
                  <div>
                      Please select the following submission frequency depending on the size of your organization:
                    <br />
                    <br />
                    <ol>
                      <li>Monthly - If your organization covers greater than or equal to 2,000 members.</li>
                      <li>Quarterly - If your organization covers between 500 and 1,999 members.</li>
                      <li>Annually - If your organization covers between 200 and 499 members.</li>
                    </ol>
                  </div>
                </Tooltip>
              </span>
            )}
          </label>

          <div className="col-sm-3">
            <select
              onChange={props.onInputChange}
              value={props.form?.submission_schedule?.frequency ?? ""}
              name="submission_schedule[frequency]"
              id="submission_schedule_frequency"
              required={true}
              className="form-control required"
            >
              <option />
              {/* Do not change values! They are parsed by the API exactly as-is. */}
              {props.monthly && <option value="Monthly">Monthly</option>}
              {props.quarterly && <option value="Quarterly">Quarterly</option>}
              {props.biannually && <option value="Bi-annually">Bi-annually</option>}
              {props.annually && <option value="Annually">Annually</option>}
            </select>
          </div>
        </div>
      </div>
    </div>
  )
}


/**
 * Legacy top-level comments component that takes the entire context as props.
 *
 * @param {object} props props
 * @param {any} props.form props.form
 * @param {Function} props.setFormValue props.setFormValue
 * @returns {object} JSX
 */
export function TopLevelCommentsField({
  form,
  setFormValue,
}: {
  form?: data.IRegistrationForm | null
  setFormValue: SetFormValue
}) {
  const onChange = useCallback((value: string) => {
    setFormValue(["additional_contacts", "text"], value)
  }, [setFormValue])

  return (
    <CommentsField
      value={form?.additional_contacts?.text ?? ""}
      onChange={onChange}
      required={false}
      id="additional_contacts_text"
      name="additional_contacts[text]"
    />
  )
}


export interface ICommentsFieldProps {
  value: string
  required?: boolean
  id?: string
  name?: string
  onChange: (value: string) => void
}
/**
 * Re-usable comments field.
 * First functional component in submitter registration. :)
 *
 * @param {object} props the props
 * @param {string} props.value the current value
 * @param {Function} props.onChange change event handler (receives new value as argument)
 * @param {boolean} props.required optionally make the input required (default false)
 * @param {string} props.id input id, default: "additional_contacts_text"
 * @param {string} props.name input name, default: "additional_contacts[text]"
 * @returns {object} the component
 */
export function CommentsField(props: ICommentsFieldProps) {
  const onChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    props.onChange(e.currentTarget.value)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- correct, linter/compiler is confused
  }, [props.onChange])

  // TODO: make required work correctly
  const required = props.required || false
  const id = props.id || "additional_contacts_text"
  const name = props.name || "additional_contacts[text]"

  return (
    <div className="panel panel-default">
      <div className="panel-heading">
        <h1 className="panel-title">
          <span className={required ? "required" : ""}>Comments</span>
        </h1>
      </div>
      <div className="panel-body">
        <label
          style={{ fontWeight: "normal", marginBottom: "2rem" }}
          htmlFor={id}
          className={required ? "required" : ""}
        >
          Please provide any additional information about your organization that may be helpful as Onpoint processes your
          data submissions, including significant updates since the previous year's registration, such as changes in lines
          of business or enrollment.
        </label>

        <div className="form-group row">
          <div className="col-sm-12">
            <textarea
              onChange={onChange}
              value={props.value}
              name={name}
              id={id}
              className="form-control"
              style={{ height: 300 }}
              maxLength={1000}
              required={required}
              data-fv-notempty={required ? "true" : ""}
              data-fv-notempty-message={required ? "This field is required" : ""}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
